import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { SupabaseService } from '../services/supabase/supabase.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { distinctUntilChanged, filter, forkJoin, switchMap } from 'rxjs';
import { AppService } from '../services/app/app.service';
import { FileService } from '../services/file/file-service';
import { MatButtonModule } from '@angular/material/button';
import { TenantsService } from '../services/tenants/tenants.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatToolbarModule, MatIconModule, MatButtonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private supabase = inject(SupabaseService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private fileService = inject(FileService);
  private tenantService = inject(TenantsService);
  headerTitle = inject(AppService).headerTitle;
  public readonly appName = inject(AppService).appName;

  title = 'echo-ui';
  showHeader = false;
  showFooter = false;
  circleImgSrc = '';
  imgSrc = '';

  ngOnInit(): void {
    this.initSupabaseAuthChanges();
    this.listenOnNavigationChanges();
    this.loadBackground();
  }

  /**
   * Listen to navigation changes to determine if we are on the login screen and disable the header
   * @private
   */
  private listenOnNavigationChanges(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationStart),
      )
      .subscribe((event: NavigationStart) => {
        this.showHeader = !event.url.includes('/login');
        this.showFooter = event.url.split('/').length > 2;
      });
  }

  private initSupabaseAuthChanges(): void {
    this.supabase.authChanges((event, session) => {
      console.log(event, session);
      this.supabase.session = session;

      if (event === `SIGNED_OUT`) {
        if (this.tenantService.activeTenant) {
          // as there is currently no real home page, we go to login page again
          // this forces angular to reload the home component
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              void this.router.navigate(['/', this.tenantService.activeTenant]);
            });
        } else {
          void this.router.navigate(['/']);
        }
      }
    });
  }

  /**
   * Loads the global background
   * This must be done as a subscription as the AppComponent is initialized before the value is set by the routing
   * @private
   */
  private loadBackground(): void {
    this.tenantService.activeTenant$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        distinctUntilChanged(),
        switchMap((tenant) => {
          return forkJoin({
            bg: this.fileService.getFile('bg_login', tenant + '.jpg'),
            avatar: this.fileService.getAsset('avatar.png'),
          });
        }),
      )
      .subscribe((results) => {
        this.imgSrc = URL.createObjectURL(results.bg!);
        this.circleImgSrc = URL.createObjectURL(results.avatar!);
      });
  }

  logout(): void {
    void this.supabase.signOut();
  }
}
